import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { httpGet, httpPost, httpPatch, httpDelete } from "../components/helpers/httpMethods";
import "./reader.css";
import { layoutPage, hardToRead, looseHeadline } from "./textLayout";

// The new reader. Draws pages with pdf.js: fit to screen, 1 or 2 pages, page list, "In this edition",
// search inside the paper, bookmarks, pick up where you left off, previous/next edition, share.
// Access, likes and comments stay in appView.js.
pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@2.6.347/build/pdf.worker.min.js";

const dpr = () => Math.min(window.devicePixelRatio || 1, 2.5);
const isPhone = () => window.innerWidth < 768;
const store = {
  get: (k, d) => { try { const v = localStorage.getItem(k); return v === null ? d : JSON.parse(v); } catch (e) { return d; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } },
};
const fmtDay = (d, long = true) => {
  const x = d ? new Date(d) : null;
  if (!x || Number.isNaN(x.getTime())) return "";
  return x.toLocaleDateString("en-GB", long
    ? { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Africa/Lagos" }
    : { weekday: "short", day: "numeric", month: "short", timeZone: "Africa/Lagos" });
};
const titleOf = (e, c) => (c && c.name) || (e && ((e.publication && e.publication.name) || e.publicationName || e.name)) || "Edition";
const titleCase = (s) => (s && s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase()) : s);
// Page text arrives as extracted lines: join lines into paragraphs and mend words split across lines.
const paragraphs = (body) => String(body || "")
  .replace(/\r/g, "")
  .split(/\n\s*\n+/)
  .map((p) => p.replace(/([a-z])-\n([a-z])/g, "$1$2").replace(/\s*\n\s*/g, " ").replace(/\s{2,}/g, " ").trim())
  .filter((p) => p.length > 1);
// The page's shape (headlines, bylines, paragraphs); plain paragraphs if anything unexpected turns up.
const pageBlocks = (body, headline) => {
  try { return layoutPage(body, headline); } catch (e) { return paragraphs(body).map((text) => ({ t: "p", text })); }
};
const marked = (s) => String(s || "").split(/(⟦[^⟧]*⟧)/).map((part, i) => (part.startsWith("⟦") ? <mark key={i}>{part.slice(1, -1)}</mark> : part));

const I = {
  back: <path d="M15 18l-6-6 6-6" />,
  grid: <><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></>,
  list: <><path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4.5" cy="6" r="1" /><circle cx="4.5" cy="12" r="1" /><circle cx="4.5" cy="18" r="1" /></>,
  mark: <path d="M6 4h12v16l-6-4-6 4z" />,
  heart: <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />,
  chat: <path d="M5 5h14v10H9l-4 4z" />,
  share: <><circle cx="6" cy="12" r="2" /><circle cx="18" cy="6" r="2" /><circle cx="18" cy="18" r="2" /><path d="M8 11l8-4M8 13l8 4" /></>,
  full: <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
};
const Icon = ({ name, filled }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{I[name]}</svg>
);

function PageCanvas({ doc, number, scale }) {
  const ref = useRef(null);
  useEffect(() => {
    let task = null;
    let alive = true;
    doc.getPage(number).then((page) => {
      if (!alive || !ref.current) return;
      const vp = page.getViewport({ scale: scale * dpr() });
      const c = ref.current;
      c.width = Math.floor(vp.width);
      c.height = Math.floor(vp.height);
      c.style.width = `${Math.floor(vp.width / dpr())}px`;
      c.style.height = `${Math.floor(vp.height / dpr())}px`;
      task = page.render({ canvasContext: c.getContext("2d"), viewport: vp });
      task.promise.catch(() => {});
    });
    return () => { alive = false; if (task) task.cancel(); };
  }, [doc, number, scale]);
  return <canvas ref={ref} className="rd-page" aria-label={`Page ${number}`} />;
}

function Thumb({ doc, number, active, onPick }) {
  const ref = useRef(null);
  const wrap = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!wrap.current || seen) return undefined;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) setSeen(true); }, { rootMargin: "200px" });
    io.observe(wrap.current);
    return () => io.disconnect();
  }, [seen]);
  useEffect(() => {
    if (!seen) return undefined;
    let task = null;
    doc.getPage(number).then((page) => {
      if (!ref.current) return;
      const base = page.getViewport({ scale: 1 });
      const vp = page.getViewport({ scale: (72 / base.width) * dpr() });
      ref.current.width = vp.width;
      ref.current.height = vp.height;
      task = page.render({ canvasContext: ref.current.getContext("2d"), viewport: vp });
      task.promise.catch(() => {});
    });
    return () => { if (task) task.cancel(); };
  }, [seen, doc, number]);
  useEffect(() => { if (active && wrap.current && wrap.current.scrollIntoView) wrap.current.scrollIntoView({ block: "nearest" }); }, [active]);
  return (
    <button type="button" ref={wrap} className={`rd-thumb${active ? " on" : ""}`} onClick={() => onPick(number)} aria-label={`Go to page ${number}`} aria-current={active ? "page" : undefined}>
      <canvas ref={ref} />
      <span>{number}</span>
    </button>
  );
}

export default function ReaderShell({ fileUrl, editionId, edition, initialPage, onBack, onLike, onComment, onPageChange, commentCount, onTextView }) {
  const root = useRef(null);
  const stage = useRef(null);
  const [doc, setDoc] = useState(null);
  const [pct, setPct] = useState(0);
  const [error, setError] = useState("");
  const [pages, setPages] = useState(0);
  const startAt = Math.max(1, (Number(initialPage) || 0) + 1);
  const [page, setPage] = useState(startAt);
  const [zoom, setZoom] = useState(1);
  const [box, setBox] = useState({ w: 800, h: 600, top: 64 });
  const [base, setBase] = useState(null);
  const [mode, setMode] = useState(store.get("tns-reader-mode", "auto")); // auto | one | two
  const [rail, setRail] = useState(store.get("tns-reader-rail", true) && window.innerWidth >= 1024);
  const [panel, setPanel] = useState(false);
  const [grid, setGrid] = useState(false);
  const [tries, setTries] = useState(0);
  const [contents, setContents] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [marks, setMarks] = useState(store.get(`tns-reader-marks:${editionId}`, []));
  const [resume, setResume] = useState(null);
  const [toast, setToast] = useState("");
  const [notes, setNotes] = useState({});
  const [social, setSocial] = useState(null);
  const [noteFor, setNoteFor] = useState(0);
  const [noteText, setNoteText] = useState("");
  // Saved pages live on the server (they follow the reader to every device). Pages saved on this device before are uploaded once.
  useEffect(() => {
    let on = true;
    httpGet(`me/saves/${editionId}`).then(async (r) => {
      if (!on || !r || r.er || !r.data) return;
      const server = r.data.saves || [];
      const local = store.get(`tns-reader-marks:${editionId}`, []);
      const missing = local.filter((x) => !server.some((s) => Number(s.pageNumber) === x));
      for (const x of missing) await httpPost("me/saves", { editionId, pageNumber: x });
      const all = Array.from(new Set([...server.map((s) => Number(s.pageNumber)), ...missing])).sort((a, b) => a - b);
      const n = {}; server.forEach((s) => { if (s.note) n[Number(s.pageNumber)] = s.note; });
      if (on) { setMarks(all); setNotes(n); store.set(`tns-reader-marks:${editionId}`, all); }
    });
    httpGet(`me/edition_social/${editionId}`).then((r) => { if (on && r && r.data && !r.er) setSocial(r.data); });
    return () => { on = false; };
  }, [editionId]);
  const [mode2, setMode2] = useState("pages"); // pages | text
  const [text, setText] = useState(null); // { pages, page, headline, body } or { error }
  const [textSize, setTextSize] = useState(store.get("tns-reader-text-size", 18));
  const [textDark, setTextDark] = useState(store.get("tns-reader-text-dark", false));
  const textTop = useRef(null);

  useEffect(() => {
    const measure = () => {
      let top = 0;
      document.querySelectorAll("header, nav, .navbar, [class*='Navbar'], [class*='navbar']").forEach((el) => {
        if (root.current && root.current.contains(el)) return;
        const r = el.getBoundingClientRect();
        if (r.top <= 1 && r.height > 20 && r.height < 200 && r.bottom > top) top = r.bottom;
      });
      setBox((b) => ({ ...b, top: Math.round(top || 64) }));
    };
    measure();
    document.body.classList.add("tns-reader-on");
    window.addEventListener("resize", measure);
    return () => { document.body.classList.remove("tns-reader-on"); window.removeEventListener("resize", measure); };
  }, []);

  useEffect(() => {
    if (!fileUrl) return undefined;
    setError(""); setDoc(null); setPct(0);
    const task = pdfjsLib.getDocument({ url: fileUrl });
    task.onProgress = (p) => { if (p && p.total) setPct(Math.round((100 * p.loaded) / p.total)); };
    let alive = true;
    task.promise.then(async (d) => {
      if (!alive) return;
      const p1 = await d.getPage(1);
      const v = p1.getViewport({ scale: 1 });
      setBase({ w: v.width, h: v.height });
      setPages(d.numPages);
      setDoc(d);
      const last = editionId ? store.get(`tns-reader-last:${editionId}`, 0) : 0;
      if (startAt === 1 && last > 1 && last <= d.numPages) setResume(last);
    }).catch(() => { if (alive) setError("We couldn’t open this paper. Please check your connection and try again."); });
    return () => { alive = false; task.destroy(); };
  }, [fileUrl, tries]);

  useEffect(() => {
    if (!editionId) return undefined;
    let alive = true;
    let tries = 0;
    let timer = null;
    // While the edition's text is being prepared, check again every 20 seconds (up to 5 minutes).
    const load = () => httpGet(`reader/${editionId}/contents`).then((r) => {
      if (!alive || !r || !r.data || !r.data.edition) return;
      setContents(r.data);
      if (r.data.preparing && tries < 15) { tries += 1; timer = setTimeout(load, 20000); }
    });
    load();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [editionId]);

  useEffect(() => {
    const el = stage.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setBox((b) => ({ ...b, w: el.clientWidth, h: el.clientHeight })));
    ro.observe(el);
    return () => ro.disconnect();
  }, [doc, rail, panel]);

  useEffect(() => { if (editionId && doc) store.set(`tns-reader-last:${editionId}`, page); }, [page, doc, editionId]);
  useEffect(() => { if (!toast) return undefined; const t = setTimeout(() => setToast(""), 2500); return () => clearTimeout(t); }, [toast]);

  // Search inside this paper (short pause while typing).
  useEffect(() => {
    if (!editionId) return undefined;
    const q = query.trim();
    if (q.length < 2) { setResults(null); return undefined; }
    const t = setTimeout(() => {
      httpGet(`reader/${editionId}/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r && r.data && Array.isArray(r.data.results) ? r.data.results : []));
    }, 350);
    return () => clearTimeout(t);
  }, [query, editionId]);

  useEffect(() => {
    if (mode2 !== "text" || !editionId) return undefined;
    let alive = true;
    setText((t) => (t && !t.error ? { ...t, loading: true } : { loading: true }));
    httpGet(`reader/${editionId}/text?page=${page}`).then((r) => {
      if (!alive) return;
      if (r && r.data && Array.isArray(r.data.pages)) {
        setText(r.data);
        if (r.data.page && r.data.page !== page) setPage(r.data.page);
        if (textTop.current) textTop.current.scrollTo(0, 0);
        return;
      }
      const msg = (r && r.er && (r.er.message || String(r.er))) || "We couldn’t load the text. Please try again.";
      const paper = r && r.er && r.er.data && r.er.data.subscribe ? r.er.data.publication : null;
      setText({ error: msg, archive: /archive/i.test(msg), login: /log ?in|token|unauthori/i.test(msg), paper });
    });
    return () => { alive = false; };
  }, [mode2, page, editionId]);
  const textStep = (dir) => {
    if (!text || !text.pages || !text.pages.length) return;
    const i = text.pages.indexOf(text.page);
    const n = text.pages[Math.min(Math.max(0, i + dir), text.pages.length - 1)];
    if (n && n !== text.page) setPage(n);
  };
  const sizeBy = (d) => setTextSize((v) => { const n = Math.min(26, Math.max(14, v + d)); store.set("tns-reader-text-size", n); return n; });
  const toggleDark = () => setTextDark((v) => { store.set("tns-reader-text-dark", !v); return !v; });
  const showText = contents && (contents.extracted || contents.preparing);
  const textBlocks = useMemo(() => (text && text.body ? pageBlocks(text.body, text.headline) : []), [text && text.body, text && text.headline]); // eslint-disable-line react-hooks/exhaustive-deps
  const textHard = useMemo(() => (text && text.body ? hardToRead(text.body) : false), [text && text.body]); // eslint-disable-line react-hooks/exhaustive-deps
  const textLoose = useMemo(() => (text && text.body ? looseHeadline(text.body, text.headline) : null), [text && text.body, text && text.headline]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoTwo = box.w >= 1000 && box.w > box.h;
  const spread = !isPhone() && (mode === "two" || (mode === "auto" && autoTwo));
  const view = (() => {
    if (!doc) return [];
    if (!spread || page === 1) return [page];
    const left = page % 2 === 0 ? page : page - 1;
    return left + 1 <= pages ? [left, left + 1] : [left];
  })();
  const top0 = () => { if (stage.current) stage.current.scrollTo(0, 0); };
  const go = (n) => { setPage(Math.min(Math.max(1, n), pages || 1)); setZoom(1); setGrid(false); if (isPhone()) setPanel(false); top0(); };
  const step = (dir) => {
    if (!pages) return;
    let next;
    if (!spread) next = page + dir;
    else if (dir > 0) next = page === 1 ? 2 : view[view.length - 1] + 1;
    else next = page <= 3 ? 1 : view[0] - 2;
    setPage(Math.min(Math.max(1, next), pages));
    setZoom(1);
    top0();
  };
  const chooseMode = (m) => { setMode(m); store.set("tns-reader-mode", m); };
  const toggleRail = () => { setRail((r) => { store.set("tns-reader-rail", !r); return !r; }); };

  const fit = (() => {
    if (!base) return 1;
    const gap = 8;
    const availW = (box.w - 32 - gap * (view.length - 1)) / Math.max(1, view.length);
    const availH = box.h - 32;
    return isPhone() ? availW / base.w : Math.min(availW / base.w, availH / base.h);
  })();
  const scale = Math.max(0.2, fit * zoom);

  const full = () => {
    const el = root.current;
    if (!document.fullscreenElement && el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    else if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
  };
  const marked0 = marks.includes(view[0] || page);
  const toggleMark = () => {
    const p = view[0] || page;
    const removing = marks.includes(p);
    const next = removing ? marks.filter((x) => x !== p) : [...marks, p].sort((a, b) => a - b);
    setMarks(next);
    store.set(`tns-reader-marks:${editionId}`, next);
    setToast(removing ? `Page ${p} removed from saved` : `Page ${p} saved`);
    if (removing) setNoteFor(0); else { setNoteFor(p); setNoteText(notes[p] || ""); }
    (removing ? httpDelete(`me/saves/${editionId}/${p}`) : httpPost("me/saves", { editionId, pageNumber: p })).catch(() => {});
  };
  const saveNote = () => {
    const p = noteFor; const t = noteText.trim();
    setNoteFor(0); setNotes({ ...notes, [p]: t });
    if (t) { httpPatch(`me/saves/${editionId}/${p}`, { note: t }); setToast("Note saved"); }
  };
  const fmtCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n));
  const likeTap = async () => {
    const was = social || { liked: false, likes: 0 };
    const now = !was.liked;
    setSocial({ ...was, liked: now, likes: Math.max(0, (was.likes || 0) + (now ? 1 : -1)), pop: now ? Date.now() : 0 });
    if (now && navigator.vibrate) navigator.vibrate(12);
    try { await onLike(); } catch (e) { setSocial(was); return; }
    setTimeout(() => httpGet(`me/edition_social/${editionId}`).then((r) => { if (r && r.data && !r.er) setSocial((cur) => ({ ...r.data, pop: cur && cur.pop })); }), 600);
  };
  const share = async () => {
    const name = titleCase(titleOf(edition, contents && contents.edition));
    const url = `${window.location.origin}/publication/${encodeURIComponent(name.toUpperCase() === name ? name : titleOf(edition, contents && contents.edition))}`;
    const text = `I'm reading ${name} on TheNewspaperStand`;
    try {
      if (navigator.share) { await navigator.share({ title: name, text, url }); return; }
      await navigator.clipboard.writeText(`${text}: ${url}`);
      setToast("Link copied");
    } catch (e) { /* share cancelled */ }
  };
  const openEdition = (id) => { window.location.href = `/view_single_paper/${id}`; };

  const onKey = useCallback((e) => {
    if (/input|textarea|select/i.test((e.target && e.target.tagName) || "")) return;
    if (mode2 === "text" && (e.key === "ArrowRight" || e.key === "ArrowLeft")) { textStep(e.key === "ArrowRight" ? 1 : -1); return; }
    if (e.key === "ArrowRight" || e.key === "PageDown") step(1);
    else if (e.key === "ArrowLeft" || e.key === "PageUp") step(-1);
    else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(4, z + 0.25));
    else if (e.key === "-") setZoom((z) => Math.max(1, z - 0.25));
    else if (e.key === "0") setZoom(1);
    else if (e.key === "f" || e.key === "F") full();
    else if (e.key === "b" || e.key === "B") toggleMark();
    else if (e.key === "Escape") { setPanel(false); setGrid(false); }
  });
  useEffect(() => { window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, [onKey]);

  // The Stand panel follows the page being read, and can send the reader to a page.
  useEffect(() => { if (onPageChange) onPageChange(view[0] || page); }, [page, spread]);
  useEffect(() => {
    const onGo = (ev) => { if (ev.detail) go(Number(ev.detail)); };
    window.addEventListener("tns:reader-go", onGo);
    return () => window.removeEventListener("tns:reader-go", onGo);
  }, [pages]);

  // Casual-copy guard: no Save or Print shortcuts while reading (right-click and drag are handled on .rd-root).
  useEffect(() => {
    const onGuard = (ev) => {
      if ((ev.ctrlKey || ev.metaKey) && /^[sp]$/i.test(ev.key)) ev.preventDefault();
    };
    window.addEventListener("keydown", onGuard, true);
    return () => window.removeEventListener("keydown", onGuard, true);
  }, []);

  const touch = useRef({ x: 0, y: 0, t: 0, last: 0 });
  const onTouchStart = (e) => { const p = e.touches[0]; touch.current = { ...touch.current, x: p.clientX, y: p.clientY, t: Date.now() }; };
  const onTouchEnd = (e) => {
    const p = e.changedTouches[0];
    const dx = p.clientX - touch.current.x;
    const dy = p.clientY - touch.current.y;
    const now = Date.now();
    if (zoom === 1 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5 && now - touch.current.t < 600) { step(dx < 0 ? 1 : -1); return; }
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
      if (now - touch.current.last < 300) setZoom((z) => (z > 1 ? 1 : 2.2));
      touch.current.last = now;
    }
  };

  useEffect(() => {
    if (!doc) return;
    const after = view[view.length - 1] || 1;
    [after + 1, after + 2].filter((n) => n <= pages).forEach((n) => doc.getPage(n).catch(() => {}));
  }, [doc, page, spread]);

  const e = edition || {};
  const name = titleCase(titleOf(e, contents && contents.edition));
  const date = fmtDay((contents && contents.edition && contents.edition.publishedDate) || e.publishedDate);
  const label = view.length > 1 ? `Pages ${view[0]}–${view[1]} of ${pages}` : `Page ${view[0] || page} of ${pages || "…"}`;
  const heads = (contents && contents.pages) || [];

  const Contents = (
    <aside className="rd-panel" aria-label="In this edition">
      <div className="rd-panel-head">
        <strong>In this edition</strong>
        <button type="button" className="rd-icon" onClick={() => setPanel(false)} aria-label="Close"><Icon name="close" /></button>
      </div>
      <label className="rd-search">
        <span className="rd-sr">Search this paper</span>
        <input type="search" value={query} onChange={(ev) => setQuery(ev.target.value)} placeholder="Search this paper" />
      </label>
      <div className="rd-panel-body">
        {results !== null ? (
          results.length ? results.map((r) => (
            <button type="button" key={`s${r.page}`} className="rd-row" onClick={() => go(r.page)}>
              <span className="rd-row-text"><strong>{r.headline || `Page ${r.page}`}</strong><small>{marked(r.snippet)}</small></span>
              <span className="rd-pg">p. {r.page}</span>
            </button>
          )) : <p className="rd-note">{contents && !contents.extracted ? "Search isn’t ready for this edition yet." : `No matches for “${query.trim()}”.`}</p>
        ) : (
          <>
            {marks.length ? (
              <div className="rd-group">
                <span className="rd-group-label">Your saved pages</span>
                {marks.map((p) => <button type="button" key={`m${p}`} className="rd-row" onClick={() => go(p)}><span className="rd-row-text"><strong>Page {p}</strong>{notes[p] ? <small>{notes[p]}</small> : null}</span><span className="rd-pg">p. {p}</span></button>)}
              </div>
            ) : null}
            <div className="rd-group">
              <span className="rd-group-label">Headlines</span>
              {heads.length ? heads.map((h) => (
                <button type="button" key={`h${h.page}`} className={`rd-row${view.includes(h.page) ? " on" : ""}`} onClick={() => go(h.page)}>
                  <span className="rd-row-text"><strong>{h.headline}</strong></span><span className="rd-pg">p. {h.page}</span>
                </button>
              )) : <p className="rd-note">{!contents ? "Loading…" : contents.preparing ? "Preparing headlines and search for this edition… this usually takes under a minute." : contents.textStatus === "image_only" ? "This edition was scanned as pictures, so headlines and search aren’t available for it." : "Headlines for this edition aren’t available yet. Use the pages list to browse."}</p>}
            </div>
          </>
        )}
      </div>
    </aside>
  );

  return (
    <div
      className="rd-root"
      ref={root}
      style={{ top: box.top }}
      role="region"
      aria-label="Paper reader"
      onContextMenu={(ev) => { if (!/input|textarea/i.test(ev.target.tagName)) ev.preventDefault(); }}
      onDragStart={(ev) => ev.preventDefault()}
    >
      <header className="rd-bar">
        <button type="button" className="rd-icon" onClick={onBack} aria-label="Back"><Icon name="back" /></button>
        <div className="rd-title"><span className="rd-name">{name}</span><span className="rd-sub">{date}{pages ? ` · ${pages} pages` : ""}</span></div>
        <span className="rd-grow" />
        {showText ? (
          <div className="rd-seg" role="group" aria-label="Reading view">
            <button type="button" className={mode2 === "pages" ? "on" : ""} aria-pressed={mode2 === "pages"} onClick={() => setMode2("pages")}>Pages</button>
            <button type="button" className={mode2 === "text" ? "on" : ""} aria-pressed={mode2 === "text"} onClick={() => setMode2("text")}>Text</button>
          </div>
        ) : onTextView ? (
          <div className="rd-seg" role="group" aria-label="Reading view">
            <span className="on">Pages</span>
            <button type="button" onClick={onTextView}>Text</button>
          </div>
        ) : null}
        {mode2 === "pages" ? <div className="rd-seg rd-hide-sm" role="group" aria-label="Pages at a time">
          {[["auto", "Auto"], ["one", "1 page"], ["two", "2 pages"]].map(([k, l]) => (
            <button type="button" key={k} className={mode === k ? "on" : ""} aria-pressed={mode === k} onClick={() => chooseMode(k)}>{l}</button>
          ))}
        </div> : null}
        {mode2 === "pages" ? <div className="rd-zoom" role="group" aria-label="Zoom">
          <button type="button" className="rd-icon" onClick={() => setZoom((z) => Math.max(1, z - 0.25))} aria-label="Zoom out" disabled={zoom <= 1}>−</button>
          <button type="button" className="rd-fit" onClick={() => setZoom(1)} aria-label="Fit to screen">{zoom === 1 ? "Fit" : `${Math.round(zoom * 100)}%`}</button>
          <button type="button" className="rd-icon" onClick={() => setZoom((z) => Math.min(4, z + 0.25))} aria-label="Zoom in">+</button>
        </div> : (
          <div className="rd-zoom" role="group" aria-label="Text size">
            <button type="button" className="rd-icon" onClick={() => sizeBy(-1)} aria-label="Smaller text" disabled={textSize <= 14}>A−</button>
            <button type="button" className="rd-icon" onClick={() => sizeBy(1)} aria-label="Larger text" disabled={textSize >= 26}>A+</button>
            <button type="button" className="rd-icon" onClick={toggleDark} aria-label={textDark ? "Light page" : "Dark page"} aria-pressed={textDark}>{textDark ? "☀" : "☾"}</button>
          </div>
        )}
        <button type="button" className="rd-icon rd-hide-sm" onClick={() => (window.innerWidth < 1024 ? setGrid(true) : toggleRail())} aria-label="Pages list" aria-pressed={rail}><Icon name="grid" /></button>
        <button type="button" className="rd-icon" onClick={() => setPanel((p) => !p)} aria-label="In this edition and search" aria-pressed={panel}><Icon name="list" /></button>
        <button type="button" className="rd-icon rd-hide-sm" onClick={toggleMark} aria-label={marked0 ? "Remove bookmark" : "Bookmark this page"} aria-pressed={marked0}><Icon name="mark" filled={marked0} /></button>
        {onLike ? <button type="button" key={social && social.pop ? social.pop : "like"} className={`rd-icon rd-hide-sm rd-like${social && social.liked ? " on" : ""}${social && social.pop ? " pop" : ""}`} onClick={likeTap} aria-label={social && social.liked ? "Unlike this edition" : "Like this edition"} aria-pressed={!!(social && social.liked)}><Icon name="heart" filled={!!(social && social.liked)} />{social && social.likes >= 10 ? <span className="rd-like-n">{fmtCount(social.likes)}</span> : null}</button> : null}
        {onComment ? <button type="button" className="rd-icon rd-count" onClick={() => onComment(view[0] || page)} aria-label={`Comments (${commentCount || 0})`}><Icon name="chat" /><span>{commentCount || 0}</span></button> : null}
        <button type="button" className="rd-icon rd-hide-sm" onClick={share} aria-label="Share"><Icon name="share" /></button>
        <button type="button" className="rd-icon rd-hide-sm" onClick={full} aria-label="Full screen"><Icon name="full" /></button>
      </header>

      <div className="rd-body">
        {mode2 === "text" ? (
          <main className={`rd-text${textDark ? " dark" : ""}`} ref={textTop} aria-live="polite">
            <article className="rd-article" style={{ fontSize: textSize }}>
              {!text || (text.loading && !text.pages) ? <p className="rd-t-note">Loading the text…</p> : text.error ? (
                <div className="rd-t-note">
                  <p>{text.error}</p>
                  {text.archive ? <a className="rd-btn" href={`/archive?buy=${editionId}`}>Buy this edition</a> : null}
                  {text.login ? <a className="rd-btn" href="/login">Log in</a> : null}
                  {text.paper ? <a className="rd-btn" href={`/publication/${encodeURIComponent(text.paper)}`}>See plans and single copies</a> : null}
                </div>
              ) : !text.pages.length ? (
                <p className="rd-t-note">{contents && contents.preparing ? "The text for this edition is being prepared. It usually takes under a minute; this page updates by itself." : "There’s no text for this edition yet. Use the Pages view to read it."}</p>
              ) : (
                <>
                  <span className="rd-t-kicker">Page {text.page} of {pages || "…"}{text.loading ? " · loading…" : ""}</span>
                  {textHard ? <p className="rd-t-hard">Part of this page, often an advert, can’t be turned into text. <button type="button" className="rd-t-link" onClick={() => setMode2("pages")}>See it on the page</button></p> : null}
                  {textLoose ? <h2 className="rd-t-head">{textLoose}</h2> : null}
                  {textBlocks.map((b, i) => {
                    if (b.t === "head") return <h2 key={i} className="rd-t-head">{b.text}</h2>;
                    if (b.t === "sub") return <h3 key={i} className="rd-t-sub">{b.text}</h3>;
                    if (b.t === "p") return <p key={i} className="rd-t-p">{b.text}</p>;
                    return <p key={i} className={`rd-t-${b.t}`}>{b.text}</p>;
                  })}
                  <div className="rd-t-nav">
                    <button type="button" className="rd-t-btn" onClick={() => textStep(-1)} disabled={text.pages.indexOf(text.page) <= 0}>‹ Previous page</button>
                    <button type="button" className="rd-t-link" onClick={() => setMode2("pages")}>See on the page</button>
                    <button type="button" className="rd-t-btn" onClick={() => textStep(1)} disabled={text.pages.indexOf(text.page) >= text.pages.length - 1}>Next page ›</button>
                  </div>
                  <p className="rd-t-small">Text taken from the printed page. Pictures, adverts and some layouts may not appear here.</p>
                </>
              )}
            </article>
          </main>
        ) : null}
        {mode2 === "pages" && rail && doc && window.innerWidth >= 1024 ? (
          <nav className="rd-rail" aria-label="Pages">
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => <Thumb key={n} doc={doc} number={n} active={view.includes(n)} onPick={go} />)}
          </nav>
        ) : null}
        <main className={`rd-stage${zoom > 1 || isPhone() ? " scroll" : ""}`} ref={stage} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} style={mode2 === "text" ? { display: "none" } : undefined}>
          {error ? (
            <div className="rd-msg"><p>{error}</p><button type="button" className="rd-btn" onClick={() => setTries((t) => t + 1)}>Try again</button></div>
          ) : !doc ? (
            <div className="rd-msg" role="status"><div className="rd-load"><span style={{ width: `${pct}%` }} /></div><p>Opening the paper… {pct ? `${pct}%` : ""}</p></div>
          ) : (
            <div className="rd-spread">{view.map((n) => <PageCanvas key={`${n}-${scale.toFixed(3)}`} doc={doc} number={n} scale={scale} />)}</div>
          )}
          {doc && !isPhone() ? (
            <>
              <button type="button" className="rd-turn prev" onClick={() => step(-1)} disabled={view[0] <= 1} aria-label="Previous page">‹</button>
              <button type="button" className="rd-turn next" onClick={() => step(1)} disabled={view[view.length - 1] >= pages} aria-label="Next page">›</button>
            </>
          ) : null}
          {resume ? (
            <div className="rd-resume" role="status">
              <span>Continue from page {resume}?</span>
              <button type="button" className="rd-btn sm" onClick={() => { go(resume); setResume(null); }}>Continue</button>
              <button type="button" className="rd-link" onClick={() => setResume(null)}>Start at the front</button>
            </div>
          ) : null}
          {toast ? <div className="rd-toast" role="status">{toast}</div> : null}
          {noteFor ? (
            <div className="rd-note" role="dialog" aria-label={`Note for page ${noteFor}`}>
              <div className="rd-note-head"><strong>Page {noteFor} saved</strong><a href="/account/library?tab=saved">View saved ›</a></div>
              <input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") saveNote(); if (e.key === "Escape") setNoteFor(0); }} maxLength={280} placeholder="Add a note (optional), e.g. Tender: apply by 10 Oct" autoFocus />
              <div className="rd-note-foot"><button type="button" onClick={() => setNoteFor(0)}>Skip</button><button type="button" className="go" onClick={saveNote}>Save note</button></div>
            </div>
          ) : null}
        </main>
        {panel ? Contents : null}
      </div>

      <footer className="rd-foot">
        {contents && contents.prev ? <button type="button" className="rd-ed rd-hide-sm" onClick={() => openEdition(contents.prev.id)}>‹ {fmtDay(contents.prev.publishedDate, false)}</button> : null}
        {isPhone() && doc ? <button type="button" className="rd-icon" onClick={() => step(-1)} aria-label="Previous page">‹</button> : null}
        <span className="rd-pos" aria-live="polite">{label}</span>
        <input className="rd-range" type="range" min="1" max={pages || 1} value={view[0] || page} onChange={(ev) => go(Number(ev.target.value))} aria-label="Go to page" />
        {isPhone() && doc ? <button type="button" className="rd-icon" onClick={() => setGrid(true)} aria-label="All pages"><Icon name="grid" /></button> : null}
        {isPhone() && doc ? <button type="button" className="rd-icon" onClick={() => step(1)} aria-label="Next page">›</button> : null}
        <span className="rd-keys">← → turn · + − zoom · B bookmark · F full screen</span>
        {contents && contents.next ? <button type="button" className="rd-ed rd-hide-sm" onClick={() => openEdition(contents.next.id)}>{fmtDay(contents.next.publishedDate, false)} ›</button> : null}
      </footer>

      {grid && doc ? (
        <div className="rd-grid" role="dialog" aria-label="All pages">
          <div className="rd-grid-head"><strong>All pages</strong><button type="button" className="rd-icon" onClick={() => setGrid(false)} aria-label="Close"><Icon name="close" /></button></div>
          <div className="rd-grid-body">{Array.from({ length: pages }, (_, i) => i + 1).map((n) => <Thumb key={n} doc={doc} number={n} active={view.includes(n)} onPick={go} />)}</div>
        </div>
      ) : null}
    </div>
  );
}
